import { useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import usePageMeta from "../hooks/usePageMeta";
import {
  IDENTIFICATION_ACTION,
  recordIdentificationAction,
} from "./identificationActionApi";
import { buildKioskQrDataFromSearch } from "./manageContext";
import { formatAppDateTime, formatAppIntlLabel, formatAppTime, getAppDisplayTimezone, getAppTodayIso, toAppDateKey } from "../utils/appDateTime";

function formatShiftDayLabel(dateString) {
  if (!dateString) return "Today";
  const key = toAppDateKey(dateString);
  if (!key) return "Today";
  if (key === getAppTodayIso()) return "Today";
  return formatAppIntlLabel(dateString, {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: getAppDisplayTimezone(),
  });
}

function formatShiftTimeRange(startDate, endDate) {
  const start = formatAppTime(startDate);
  const end = formatAppTime(endDate);
  if (!start && !end) return "";
  if (!start) return end;
  if (!end) return start;
  return `${start} – ${end}`;
}

function formatCheckinPointLabel(qrData) {
  const clientName = qrData?.clientDetails?.clientName || "Unknown Client";
  const location =
    qrData?.shiftDetails?.location ||
    qrData?.timesheetDetails?.location ||
    qrData?.clientDetails?.clientAddress ||
    "";
  if (location) return `${clientName}: ${location}`;
  return clientName;
}

export default function QRNoShift() {
  usePageMeta("Shift Not Scheduled");
  const navigate = useNavigate();
  const location = useLocation();
  const { qrData: qrDataFromState, user } = location.state || {};

  const qrData = useMemo(
    () => qrDataFromState || buildKioskQrDataFromSearch(location.search),
    [qrDataFromState, location.search],
  );

  useEffect(() => {
    if (!user?.identificationId) return;
    recordIdentificationAction(user.identificationId, {
      action: IDENTIFICATION_ACTION.UNSCHEDULED,
    }).catch((err) => {
      console.error("Failed to set identification action to unscheduled:", err);
    });
  }, [user?.identificationId]);

  const hasNextShift = Boolean(user?.nextShiftDetails);
  const nextShift = user?.nextShiftDetails;
  const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim();

  useEffect(() => {
    if (!user) {
      navigate("/checkin" + location.search);
    }
  }, [user, navigate, location.search]);

  if (!user) return null;

  const handleGotIt = () => {
    navigate("/checkin" + location.search);
  };

  const handleNeedHelp = async () => {
    await recordIdentificationAction(user?.identificationId, {
      action: IDENTIFICATION_ACTION.NEED_HELP,
      status: "I NEED HELP",
    });
    navigate("/checkin/help" + location.search);
  };

  const nextShiftLabel = nextShift
    ? [nextShift.clientName, nextShift.location].filter(Boolean).join(": ")
    : "";

  const nextShiftTimeLabel = nextShift
    ? `${formatShiftDayLabel(nextShift.startDate)} ${formatShiftTimeRange(
        nextShift.startDate,
        nextShift.endDate,
      )}`.trim()
    : "";

  return (
    <div className="h-screen bg-white flex items-center justify-center p-6 overflow-y-auto">
      <div className="max-w-lg w-full text-center">
        <h1 className="text-2xl font-normal text-gray-800 mb-8">
          You&apos;re not scheduled to be here
        </h1>

        <div className="text-left text-gray-700 space-y-6 mb-10">
          <p>
            Hey <span className="font-semibold">{displayName || user.firstName}</span>, we
            recognize you, but we can&apos;t find a shift you&apos;re scheduled for at this check
            in point:
          </p>

          <p className="font-medium text-gray-900">{formatCheckinPointLabel(qrData)}</p>

          {hasNextShift && nextShift ? (
            <>
              <p>It looks like the current shift you&apos;re scheduled for is:</p>
              <div className="space-y-1">
                <p className="font-medium text-gray-900">{nextShiftLabel}</p>
                <p className="text-gray-800">{nextShiftTimeLabel}</p>
              </div>
            </>
          ) : (
            <p className="text-gray-600">
              No other shifts were found for this date. Please contact your supervisor if you
              believe this is incorrect.
            </p>
          )}
        </div>

        <div className="space-y-4">
          <button
            type="button"
            onClick={handleGotIt}
            className="w-full text-[#DC1F00] font-semibold tracking-wide hover:opacity-80 transition-opacity"
          >
            GOT IT, I&apos;LL BE THERE
          </button>

          <button
            type="button"
            onClick={handleNeedHelp}
            className="w-full text-gray-500 text-sm font-semibold tracking-wide hover:text-gray-700 transition-colors"
          >
            I NEED HELP
          </button>
        </div>
      </div>
    </div>
  );
}
